import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CancellationState, DecryptParams } from 'modules/Cancellation/reducers/type';
import { getDecryptedData, RequestCancellationData, sendCancellationRequest } from 'helpers/api';
import { ErrorResponse } from 'helpers/type';
import { formatEncryptedSerialNumberString, trackCreateInCancellation } from 'modules/Cancellation/helper';
import { CancellationForm, CancellationFormFields } from 'modules/Cancellation/components/Form/Form';
import { generateUUID } from 'helpers/tools';
import { ENotificationType } from 'stores/reducers/notification/type';
import { RootState } from 'stores';

export const initialCancellationState: CancellationState = {
    isError: false,
    isLoading: false,
    initialSerialNumber: '',
    productName: '',
    isSubmitting: false,
    isFromProduct: false,
};

const FETCH_DECRYPT_DATA = 'FETCH_DECRYPT_DATA';
const SEND_FORM_DATA = 'SEND_FORM_DATA';

export const successBannerAction = (isFromProduct: boolean) => ({
    type: ENotificationType.Success,
    primaryMessage: isFromProduct
        ? "We've received your request! You will receive a call within 5 business days to your preferred contact number."
        : "We've received your request! You will receive a call within 5 business days to your preferred contact number. You may now close this tab.",
});
export const failedBannerAction = {
    type: ENotificationType.Danger,
    primaryMessage:
        'Sorry, an error occurred while we were processing your request. Please resubmit your cancellation request below.',
};

export const fetchDecryptedData = createAsyncThunk<
    string,
    DecryptParams,
    {
        rejectValue: ErrorResponse;
    }
>(FETCH_DECRYPT_DATA, async decryptParams => {
    const response = await getDecryptedData(decryptParams);
    return response.data;
});

export const sendFormData = createAsyncThunk<
    {
        success: boolean;
    },
    CancellationForm,
    { rejectValue: ErrorResponse; state: RootState }
>(SEND_FORM_DATA, async (formData, thunkAPI) => {
    const uuid = generateUUID();
    const data = { ...formData, uuid } as RequestCancellationData;
    const { dispatch, rejectWithValue, getState } = thunkAPI;
    try {
        const response = await sendCancellationRequest(data);
        if (response.data.success) {
            const { cancellation } = getState();
            dispatch(successBannerAction(cancellation.isFromProduct));
            trackCreateInCancellation({
                cancelReason: data[CancellationFormFields.REASON],
                status: 'success',
                uuid: data.uuid,
            });
            return response.data.success;
        } else {
            throw new Error();
        }
    } catch (e) {
        const message = e.response?.data?.message || 'send request cancellation failed';
        dispatch(failedBannerAction);
        trackCreateInCancellation({
            cancelReason: data[CancellationFormFields.REASON],
            status: 'failed',
            uuid: data.uuid,
            actualError: message,
        });
        return rejectWithValue({ message });
    }
});

const slice = createSlice({
    name: 'cancellation',
    initialState: initialCancellationState,
    reducers: {
        updateIsFromProduct(state, action: PayloadAction<boolean>) {
            state.isFromProduct = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchDecryptedData.pending, (state: CancellationState) => {
            state.isLoading = true;
            state.isError = false;
        });
        builder.addCase(fetchDecryptedData.fulfilled, (state: CancellationState, action: PayloadAction<string>) => {
            state = {
                ...state,
                ...formatEncryptedSerialNumberString(action.payload),
                isLoading: false,
                isError: false,
            };
            return state;
        });
        builder.addCase(fetchDecryptedData.rejected, state => {
            state = {
                ...initialCancellationState,
                isError: true,
                isLoading: false,
            };
            return state;
        });
        builder.addCase(sendFormData.pending, state => {
            state.isSubmitting = true;
        });
        builder.addCase(sendFormData.fulfilled, state => {
            state.isSubmitting = false;
        });
        builder.addCase(sendFormData.rejected, state => {
            state.isSubmitting = false;
        });
    },
});

export const { updateIsFromProduct } = slice.actions;

export default slice.reducer;
